/**
 * ENVIRONMENT: PRODUCTION
 * MODULE: Administrator Two-Factor Authentication (2FA)
 * PATH: src/routes/auth/Login.js
 */

import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { Row, Col, Form, Input, Button, Alert, Typography } from "antd";
import {
  SafetyCertificateOutlined,
  MailOutlined,
  LockOutlined,
  ArrowRightOutlined,
  CheckCircleFilled,
  ReloadOutlined,
  SafetyOutlined,
  ThunderboltFilled,
  GlobalOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import ReCAPTCHA from "react-google-recaptcha";
import { loginAction, sendOtpAction, verifyOtpAction } from "redux/actions/auth";
import logoImg from "assets/images/logo.png";
import "./Login.css";

const { Title, Text, Paragraph } = Typography;

const Login = () => {
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);

  // Step 1: Email + Password + Captcha -> Step 2: 6-Digit 2FA OTP
  const [step, setStep] = useState(1);

  // Form State
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mobile, setMobile] = useState("");
  const [otpValues, setOtpValues] = useState(["", "", "", "", "", ""]);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [timer, setTimer] = useState(30);

  const otpInputsRef = useRef([]);
  const recaptchaRef = useRef(null);

  const authUser = useSelector((state) => state.auth?.authUser);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (authUser && token) {
      const from = location.state?.from?.pathname || "/overviewpayin";
      navigate(from, { replace: true });
    }
  }, [authUser, location, navigate]);

  useEffect(() => {
    let interval = null;
    if (step === 2 && timer > 0) {
      interval = setInterval(() => setTimer((t) => t - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [step, timer]);

  // ==========================================
  // STEP 1: VERIFY CREDENTIALS & DISPATCH SMS OTP
  // ==========================================
  const handlePrimaryLogin = async () => {
    if (!email || !email.includes("@")) {
      setIsError(true);
      setErrorMessage("Please enter a valid administrator email address.");
      return;
    }
    if (!password || password.trim() === "") {
      setIsError(true);
      setErrorMessage("Please enter your administrator password.");
      return;
    }
    if (!captchaToken) {
      setIsError(true);
      setErrorMessage("Please complete the reCAPTCHA security verification.");
      return;
    }

    try {
      setIsError(false);
      setLoading(true);

      // 1. Verify User ID & Password
      const loginRes = await dispatch(
        loginAction({
          email: email.trim(),
          password: password,
        })
      );

      // Resolve registered admin mobile from response or fallback
      const registeredPhone =
        loginRes?.mobile ||
        loginRes?.user?.mobile ||
        loginRes?.data?.mobile ||
        "7587404000"; // fallback to known admin mobile

      setMobile(registeredPhone);

      // 2. Trigger the SMS Gateway via /login-send-otp
      await dispatch(
        sendOtpAction({
          mobile: registeredPhone,
          captcha_token: captchaToken,
        })
      );

      // 3. Move to Step 2
      setStep(2);
      setTimer(30);
      setOtpValues(["", "", "", "", "", ""]);
      setTimeout(() => otpInputsRef.current[0]?.focus(), 150);
    } catch (err) {
      setIsError(true);
      setErrorMessage(
        err?.response?.data?.message || err?.message || "Invalid administrator credentials or OTP dispatch failed."
      );
      if (recaptchaRef.current) recaptchaRef.current.reset();
      setCaptchaToken(null);
    } finally {
      setLoading(false);
    }
  };

  // ==========================================
  // STEP 2: VERIFY 6-DIGIT OTP
  // ==========================================
  const handleVerifyOtp = async () => {
    const fullOtp = otpValues.join("");
    if (fullOtp.length !== 6) {
      setIsError(true);
      setErrorMessage("Please enter the complete 6-digit verification code.");
      return;
    }

    try {
      setIsError(false);
      setLoading(true);

      await dispatch(
        verifyOtpAction({
          mobile: mobile,
          otp: fullOtp,
        })
      );
    } catch (err) {
      setIsError(true);
      setErrorMessage(
        err?.response?.data?.message || err?.message || "Invalid or expired OTP code."
      );
    } finally {
      setLoading(false);
    }
  };

  // Resend OTP in Step 2
  const handleResendOtp = async () => {
    try {
      setIsError(false);
      setResending(true);
      await dispatch(
        sendOtpAction({
          mobile: mobile,
          captcha_token: captchaToken,
        })
      );
      setTimer(30);
      setOtpValues(["", "", "", "", "", ""]);
      setTimeout(() => otpInputsRef.current[0]?.focus(), 100);
    } catch (err) {
      // If token expired, take user back to Step 1 to solve captcha
      setStep(1);
      setIsError(true);
      setErrorMessage("Session expired. Please re-enter credentials and solve captcha to resend code.");
    } finally {
      setResending(false);
    }
  };

  const handleOtpChange = (index, value) => {
    const cleanVal = value.replace(/\D/g, "").slice(-1);
    const newOtp = [...otpValues];
    newOtp[index] = cleanVal;
    setOtpValues(newOtp);

    if (cleanVal && index < 5) {
      otpInputsRef.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otpValues[index] && index > 0) {
      otpInputsRef.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!pasted) return;
    const newOtp = [...otpValues];
    for (let i = 0; i < 6; i++) {
      newOtp[i] = pasted[i] || "";
    }
    setOtpValues(newOtp);
    const nextIdx = Math.min(pasted.length, 5);
    otpInputsRef.current[nextIdx]?.focus();
  };

  return (
    <div className="admin-login-layout">
      <Row className="admin-login-row">
        {/* Left Hero Pane */}
        <Col xs={0} md={10} lg={13} className="admin-hero-pane">
          <div className="hero-gradient-overlay" />
          <div className="hero-pane-content">
            <div className="hero-brand-top">
              <div className="hero-logo-wrapper">
                <img src={logoImg} alt="MTMPAY Company Logo" className="hero-company-logo" />
              </div>
              <div className="hero-live-pill">
                <span className="live-ping" />
                BBPS Switch: Online
              </div>
            </div>

            <div className="hero-title-block">
              <span className="hero-subtitle-badge">CENTRAL OPERATIONS SUITE</span>
              <Title level={1} className="hero-main-heading">
                Unified Digital Financial &amp; BBPS Operations Hub
              </Title>
              <Paragraph className="hero-lead-text">
                Enterprise admin engine powering Bharat Connect utilities, prepaid card issuance,
                multi-line insurance, digital gift cards, and automated settlements.
              </Paragraph>
            </div>

            <div className="hero-telemetry-card">
              <div className="telemetry-item">
                <div className="telemetry-label">Gateway Latency</div>
                <div className="telemetry-value">
                  <ThunderboltFilled style={{ color: "#38bdf8", marginRight: 6 }} />
                  28 ms
                </div>
              </div>
              <div className="telemetry-divider" />
              <div className="telemetry-item">
                <div className="telemetry-label">Security Tier</div>
                <div className="telemetry-value">
                  <SafetyOutlined style={{ color: "#34d399", marginRight: 6 }} />
                  ISO 27001
                </div>
              </div>
              <div className="telemetry-divider" />
              <div className="telemetry-item">
                <div className="telemetry-label">Protocol</div>
                <div className="telemetry-value">
                  <GlobalOutlined style={{ color: "#818cf8", marginRight: 6 }} />
                  TLS 1.3
                </div>
              </div>
            </div>

            <div className="hero-trust-badges">
              <div className="trust-badge">
                <CheckCircleFilled className="badge-icon" />
                <span>NPCI Bharat Connect Certified</span>
              </div>
              <div className="trust-badge">
                <SafetyCertificateOutlined className="badge-icon" />
                <span>256-Bit Financial Encryption</span>
              </div>
            </div>
          </div>
        </Col>

        {/* Right Authentication Form Card */}
        <Col xs={24} md={14} lg={11} className="admin-form-pane">
          <div className="admin-auth-card">
            <div className="auth-card-header">
              <div className="mobile-logo-wrap">
                <img src={logoImg} alt="MTMPAY Company Logo" className="auth-mobile-logo" />
              </div>
              <Title level={2} className="auth-card-title">
                {step === 1 ? "Admin Authentication" : "Two-Factor Verification"}
              </Title>
              <Text className="auth-card-description">
                {step === 1
                  ? "Enter your administrative credentials to initialize the secure session."
                  : `Enter the 6-digit security code dispatched to ${mobile ? `+91 ${mobile.slice(0, 4)}••••${mobile.slice(-2)}` : "your phone"}.`}
              </Text>
            </div>

            {isError && (
              <Alert
                message={errorMessage}
                type="error"
                showIcon
                closable
                onClose={() => setIsError(false)}
                className="auth-error-alert"
              />
            )}

            {/* STEP 1: EMAIL + PASSWORD + RECAPTCHA */}
            {step === 1 && (
              <Form layout="vertical" onFinish={handlePrimaryLogin} className="auth-step-form">
                <Form.Item label="Administrator Email / User ID" required className="auth-form-field">
                  <Input
                    size="large"
                    prefix={<MailOutlined className="input-prefix-icon" />}
                    placeholder="e.g. info@mtmpay.in"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoFocus
                  />
                </Form.Item>

                <Form.Item label="Master Password" required className="auth-form-field">
                  <Input.Password
                    size="large"
                    prefix={<LockOutlined className="input-prefix-icon" />}
                    placeholder="Enter security password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </Form.Item>

                <Form.Item label="Identity Verification" required className="recaptcha-field">
                  <div className="recaptcha-box-wrapper">
                    <ReCAPTCHA
                      ref={recaptchaRef}
                      sitekey="6LcD21IsAAAAAObkOlNSRX2KaIuF0G3eY3b5woNl"
                      onChange={(token) => {
                        setCaptchaToken(token);
                        setIsError(false);
                      }}
                    />
                  </div>
                </Form.Item>

                <Button
                  type="primary"
                  htmlType="submit"
                  size="large"
                  block
                  loading={loading}
                  disabled={!captchaToken || !email || !password}
                  icon={<ArrowRightOutlined />}
                  className="auth-submit-btn"
                >
                  Verify Credentials &amp; Request OTP
                </Button>
              </Form>
            )}

            {/* STEP 2: 6-DIGIT OTP VERIFICATION */}
            {step === 2 && (
              <Form layout="vertical" onFinish={handleVerifyOtp} className="auth-step-form">
                <Form.Item label="One-Time Security Passcode (OTP)" required>
                  <div className="otp-digit-matrix" onPaste={handleOtpPaste}>
                    {otpValues.map((val, idx) => (
                      <input
                        key={idx}
                        ref={(el) => (otpInputsRef.current[idx] = el)}
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={1}
                        value={val}
                        onChange={(e) => handleOtpChange(idx, e.target.value)}
                        onKeyDown={(e) => handleOtpKeyDown(idx, e)}
                        className={`otp-digit-input ${val ? "filled" : ""}`}
                      />
                    ))}
                  </div>
                </Form.Item>

                <div className="otp-controls-row">
                  {timer > 0 ? (
                    <Text type="secondary" className="timer-text">
                      Resend available in <span className="timer-counter">{timer}s</span>
                    </Text>
                  ) : (
                    <Button
                      type="link"
                      onClick={handleResendOtp}
                      loading={resending}
                      icon={<ReloadOutlined />}
                      className="resend-action-btn"
                    >
                      Resend Security Code
                    </Button>
                  )}

                  <Button
                    type="link"
                    icon={<ArrowLeftOutlined />}
                    onClick={() => {
                      setStep(1);
                      setOtpValues(["", "", "", "", "", ""]);
                    }}
                    className="switch-mobile-btn"
                  >
                    Change Credentials
                  </Button>
                </div>

                <Button
                  type="primary"
                  htmlType="submit"
                  size="large"
                  block
                  loading={loading}
                  disabled={otpValues.join("").length !== 6}
                  icon={<LockOutlined />}
                  className="auth-submit-btn"
                >
                  Verify 2FA &amp; Enter Dashboard
                </Button>
              </Form>
            )}

            <div className="auth-card-footer">
              <SafetyCertificateOutlined className="footer-shield" />
              <Text type="secondary" className="footer-note">
                Authorized Personnel Only. Multi-factor administrative sessions and IP addresses
                are continuously logged under RBI IT Security Framework and DPDP Act guidelines.
              </Text>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default Login;